import React from 'react';
import { Input, Typography, Tooltip, Checkbox } from 'antd';
import { QuestionCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { TaskType } from '@/shared/domain/taskTypes';
import LabelReplacementSelect from '@/features/datasets/components/LabelReplacementSelect';
import {
    REJECTION_COLUMN_TOOLTIP,
    REJECTION_COLUMN_WIDTH,
} from '@/features/datasets/components/LabelMappingColumnsHeader';

const { Text } = Typography;

const ExtractedLabelItem = ({
    label,
    onDelete,
    onReplace,
    availableLabels,
    taskLabels = null,
    onAddNewLabel,
    labelType = "Label", // "Label", "Entity", or "Relation"
    taskType,
    isMultilabel,
    labelExample,
    /** Parent-owned map; must drive the select when tabs remount (`destroyInactiveTabPane`). */
    labelMappings = {},
    /** Single-label classification fallback "Other" column. */
    showRejectionColumn = false,
    rejectionLabelName = null,
    onSetRejectionLabel,
    /** When false, per-row column headers are hidden (rendered once by the parent). */
    showHeaders = true,
}) => {
    const shouldShowNoneOption = taskType === TaskType.classification && isMultilabel;

    const mapped = labelMappings[label];
    const replacementSelectValue = (() => {
        if (mapped === null) {
            return shouldShowNoneOption ? 'none' : label;
        }
        if (mapped !== undefined) {
            return mapped;
        }
        return label;
    })();

    const handleReplaceChange = (value) => {
        onReplace(label, value === 'none' ? null : value);
    };

    const effectiveForDotCheck =
        replacementSelectValue && replacementSelectValue !== 'none'
            ? replacementSelectValue
            : label;

    const shouldShowExampleTooltip = (
        (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) &&
        !!labelExample
    );

    const exampleTooltipTitle = shouldShowExampleTooltip ? (
        <div style={{ maxWidth: 320, maxHeight: 250, overflowY: 'auto' }}>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {labelExample}
            </div>
        </div>
    ) : null;

    return (
        <div style={{
            display: 'flex',
            gap: '12px',
            padding: '0px',
            borderRadius: '6px',
            alignItems: 'flex-start',
        }}>
            <div style={{ flex: 1 }}>
                {showHeaders && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        gap: '8px',
                        minWidth: '200px',
                    }}>
                        <Text style={{
                            fontWeight: 400,
                            color: '#222',
                            fontSize: '14px',
                            margin: 0
                        }}>
                            {labelType} extracted
                        </Text>
                        <Tooltip title={`This ${labelType.toLowerCase()} was extracted from the dataset`}>
                            <QuestionCircleOutlined style={{
                                color: '#878787',
                                fontSize: '16px',
                                cursor: 'help'
                            }} />
                        </Tooltip>
                    </div>
                )}
                <Tooltip
                    title={exampleTooltipTitle}
                    mouseEnterDelay={0.2}
                >
                    <Input
                        value={label}
                        suffix={<Tooltip title="Delete label"><DeleteOutlined onClick={() => onDelete(label)} /></Tooltip>}
                        disabled
                        style={{
                            backgroundColor: effectiveForDotCheck.includes('.') ? '#fff2f0' : '#f5f5f5',
                            border: effectiveForDotCheck.includes('.') ? '1px solid #ff4d4f' : '1px solid #d9d9d9'
                        }}
                    />
                </Tooltip>
                {effectiveForDotCheck.includes('.') && (
                    <div style={{
                        color: '#ff4d4f',
                        fontSize: '12px',
                        marginTop: '4px'
                    }}>
                        {labelType} cannot contain "." character
                    </div>
                )}
            </div>

            <div style={{ flex: 1 }}>
                <LabelReplacementSelect
                    labelType={labelType}
                    showHeader={showHeaders}
                    value={replacementSelectValue}
                    onChange={handleReplaceChange}
                    availableLabels={availableLabels}
                    taskLabels={taskLabels}
                    allowNoneOption={shouldShowNoneOption}
                    onCreateNewLabel={(newLabel) => {
                        onAddNewLabel(newLabel);
                        onReplace(label, newLabel);
                    }}
                />
            </div>

            {showRejectionColumn && (
                <div style={{ flex: `0 0 ${REJECTION_COLUMN_WIDTH}px`, minWidth: REJECTION_COLUMN_WIDTH }}>
                    {showHeaders && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, whiteSpace: 'nowrap' }}>
                            <span style={{ fontWeight: 400, color: '#222', fontSize: 14 }}>
                                Set as 'Fallback'
                            </span>
                            <span style={{ fontWeight: 400, color: '#222', fontSize: 12 }}>(Optional)</span>
                            <Tooltip
                                title={REJECTION_COLUMN_TOOLTIP}
                                zIndex={2100}
                                getPopupContainer={() => document.body}
                            >
                                <QuestionCircleOutlined style={{ color: '#878787', fontSize: 16, cursor: 'help' }} />
                            </Tooltip>
                        </div>
                    )}
                    <div style={{ height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Checkbox
                            checked={rejectionLabelName != null && rejectionLabelName === effectiveForDotCheck}
                            onChange={(e) => {
                                if (typeof onSetRejectionLabel === 'function') {
                                    onSetRejectionLabel(e.target.checked ? effectiveForDotCheck : null);
                                }
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExtractedLabelItem;
